// dashboard/Sidebar.js
// Extracted from App.js (2026-08-12 split).

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { c, CAIRN_GLYPH_PATHS, NAV_ITEMS } from './constants';
import ConditionMultiSelect from './ConditionMultiSelect';

export default function Sidebar({ view, setView, pendingCount, onLogout, onToggleUnit, onToggleLang, onSetDefaultConditionFilter, conditionTypes, profile, clinic, patients, vets, membership, canClinical, canManage }) {
  const { t } = useTranslation();
  const isDoctor  = membership?.is_doctor && !membership?.is_manager;
  const total     = [...new Set(patients.map(p => p.pet?.id).filter(Boolean))].length;
  const docCount  = vets.length;
  const [mobileOpen, setMobileOpen] = useState(false);
  const selectView = nextView => {
    setView(nextView);
    setMobileOpen(false);
  };
  return (
    <aside className={`dashboard-sidebar${mobileOpen ? ' dashboard-sidebar--open' : ''}`} style={{
      width: 220,
      backgroundColor: c.white,
      borderRight: `1px solid ${c.border}`,
      display: 'flex',
      flexDirection: 'column',
      height: '100vh',
      position: 'fixed',
      left: 0,
      top: 0,
      zIndex: 50,
      flexShrink: 0,
      overflowY: 'auto',
    }}>
      {/* Logo */}
      <div className="dashboard-sidebar-header" style={{ padding: '20px 20px 16px', borderBottom: `1px solid ${c.border}` }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
          {/* Inline SVG cairn glyph — see CAIRN_GLYPH_PATHS */}
          <svg width="24" height="24" viewBox="0 0 128 128" aria-label="Cairn">
            {CAIRN_GLYPH_PATHS.map((d, i) => <path key={i} d={d} fill={c.slate} />)}
          </svg>
          <div aria-label="Cairn Pet Health">
            <div style={{ fontSize: 14, fontWeight: 700, color: c.slate, letterSpacing: 2, lineHeight: 1.1 }}>Cairn</div>
            <div style={{ fontSize: 9, color: c.moss, letterSpacing: 2, textTransform: 'uppercase' }}>PET HEALTH</div>
          </div>
          <button
            type="button"
            className="dashboard-nav-toggle"
            aria-expanded={mobileOpen}
            aria-controls="dashboard-sidebar-content"
            aria-label={mobileOpen ? t('sidebar.closeNavigation') : t('sidebar.openNavigation')}
            onClick={() => setMobileOpen(open => !open)}
          >
            <span aria-hidden="true">{mobileOpen ? '×' : '☰'}</span>
          </button>
        </div>
        {clinic && <div style={{ fontSize: 11, color: c.textSoft, marginTop: 8, lineHeight: 1.3 }}>{clinic.name}</div>}
      </div>

      <div id="dashboard-sidebar-content" className="dashboard-sidebar-content">
      {/* Nav */}
      <nav style={{ flex: 1, padding: '12px 10px' }}>
        {NAV_ITEMS.filter(item => {
          if (item.managerOnly)          return canManage;
          if (item.id === 'dashboard')   return canClinical;
          if (item.id === 'permissions') return canManage;
          return true;
        }).map(item => {
          const active = view === item.id;
          return (
            <button
              key={item.id}
              onClick={() => selectView(item.id)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                width: '100%',
                padding: '9px 12px',
                borderRadius: 8,
                border: 'none',
                borderLeft: active ? `3px solid ${c.moss}` : '3px solid transparent',
                background: active ? `${c.moss}18` : 'transparent',
                color: active ? c.moss : c.textSoft,
                fontSize: 13,
                fontWeight: active ? 600 : 400,
                cursor: 'pointer',
                textAlign: 'left',
                marginBottom: 2,
                position: 'relative',
              }}
            >
              <span style={{ fontSize: 15, width: 18, textAlign: 'center' }}>{item.icon}</span>
              {t(item.labelKey)}
              {item.id === 'enrollments' && pendingCount > 0 && (
                <span style={{ marginLeft: 'auto', backgroundColor: c.sandstone, color: c.white, fontSize: 10, fontWeight: 700, borderRadius: 10, padding: '1px 6px' }}>
                  {pendingCount}
                </span>
              )}
            </button>
          );
        })}
      </nav>

      {/* Stats */}
      {total > 0 && (
        <div style={{ padding: '12px 16px', borderTop: `1px solid ${c.border}` }}>
          {isDoctor ? (
            <div>
              <div style={{ fontSize: 10, fontWeight: 600, color: c.textSoft, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 }}>{t('sidebar.myPatients')}</div>
              <div style={{ fontSize: 22, fontWeight: 700, color: c.slate, lineHeight: 1 }}>{total}</div>
              <div style={{ fontSize: 11, color: c.textSoft, marginTop: 2 }}>{t('sidebar.monitored')}</div>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <div>
                <div style={{ fontSize: 10, fontWeight: 600, color: c.textSoft, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>{t('sidebar.patients')}</div>
                <div style={{ fontSize: 22, fontWeight: 700, color: c.slate, lineHeight: 1 }}>{total}</div>
                <div style={{ fontSize: 11, color: c.textSoft, marginTop: 2 }}>{t('sidebar.monitored')}</div>
              </div>
              {docCount > 0 && (
                <div>
                  <div style={{ fontSize: 10, fontWeight: 600, color: c.textSoft, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>{t('sidebar.doctors')}</div>
                  <div style={{ fontSize: 22, fontWeight: 700, color: c.slate, lineHeight: 1 }}>{docCount}</div>
                  <div style={{ fontSize: 11, color: c.textSoft, marginTop: 2 }}>{t('sidebar.active')}</div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Footer */}
      <div style={{ padding: '12px 14px', borderTop: `1px solid ${c.border}` }}>
        <div style={{ fontSize: 12, color: c.slate, marginBottom: 6 }}>
          {profile?.first_name} {profile?.last_name}
        </div>
        {/* Weight unit toggle */}
        <div style={{ display: 'flex', gap: 4, marginBottom: 8 }}>
          {['lbs', 'kg'].map(u => {
            const active = u === (profile?.weight_unit_pref || 'lbs');
            return (
              <button
                key={u}
                onClick={() => onToggleUnit(u)}
                style={{
                  fontSize: 11, fontWeight: active ? 600 : 400,
                  color: active ? c.white : c.textSoft,
                  backgroundColor: active ? c.moss : 'transparent',
                  border: `1px solid ${active ? c.moss : c.border}`,
                  borderRadius: 4, padding: '2px 8px', cursor: 'pointer',
                }}
              >
                {u === 'lbs' ? t('sidebar.unitLbs') : t('sidebar.unitKg')}
              </button>
            );
          })}
        </div>
        {/* Language selector — native names (Français, Deutsch, ...) are
            language-agnostic by construction and don't need translating;
            "English" and the "(coming soon)" suffix are in the active UI
            language like everything else. */}
        <div style={{ marginBottom: 8 }}>
          <select
            value={profile?.language_pref || 'en'}
            onChange={e => onToggleLang(e.target.value)}
            style={{
              fontSize: 11, color: c.textSoft, background: 'none',
              border: `1px solid ${c.border}`, borderRadius: 4,
              padding: '2px 6px', cursor: 'pointer', width: '100%',
            }}
            aria-label={t('sidebar.displayLanguageA11y')}
          >
            <option value="en">{t('sidebar.languageEnglish')}</option>
            <option value="fr" disabled>{`Français (${t('sidebar.comingSoon')})`}</option>
            <option value="de" disabled>{`Deutsch (${t('sidebar.comingSoon')})`}</option>
            <option value="es" disabled>{`Español (${t('sidebar.comingSoon')})`}</option>
            <option value="pt" disabled>{`Português (${t('sidebar.comingSoon')})`}</option>
            <option value="it" disabled>{`Italiano (${t('sidebar.comingSoon')})`}</option>
            <option value="nl" disabled>{`Nederlands (${t('sidebar.comingSoon')})`}</option>
            <option value="ja" disabled>{`日本語 (${t('sidebar.comingSoon')})`}</option>
            <option value="ko" disabled>{`한국어 (${t('sidebar.comingSoon')})`}</option>
            <option value="zh" disabled>{`中文 (${t('sidebar.comingSoon')})`}</option>
          </select>
        </div>
        {/* Default dashboard view — per doctor, not per clinic. A
            specialist (cardiologist, neurologist, etc.) sets their own
            login to always open filtered to their specialty; other doctors
            at the same clinic keep their own default independently. */}
        {conditionTypes?.length > 0 && (
          <div style={{ marginBottom: 8 }}>
            <label htmlFor="default-condition-filter" style={{ fontSize: 10, color: c.textSoft, textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', marginBottom: 3 }}>
              {t('sidebar.myDefaultView')}
            </label>
            <ConditionMultiSelect
              id="default-condition-filter"
              ariaLabel={t('sidebar.defaultConditionFilterA11y')}
              options={[...new Set(conditionTypes)]}
              selected={profile?.default_condition_filters || []}
              onChange={onSetDefaultConditionFilter}
              openUpward
            />
          </div>
        )}
        <button
          onClick={onLogout}
          style={{ fontSize: 11, color: c.textSoft, background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
        >
          {t('sidebar.signOut')}
        </button>
      </div>
      </div>
    </aside>
  );
}
